import {
  validationObject,
  validateRequired,
  validateName,
  validateEndDate,
  validateDateRequired,
} from 'utils/validations';
import { audienceList, involvementList } from 'mocks/utils';
import eventTypes from 'static/event_categories_types.json';
import { EventInfo } from 'utils/constants/informationText';
import { EventPlaceholder } from 'utils/constants/eventStatus';
import { optionalFormField } from 'utils/methods/helpers';
import FollowupEventForm from './followupEventForm';
import {
  auditTableTransform,
  transformFollowUpEvent,
} from 'utils/DataTransform/Events/eventTransformHelper';
import {
  AuditTableColumns,
  FollowupReprtTableColumns,
} from 'utils/columns/incidentForm';
import { t } from 'i18next';
import {
  MEDIA_TYPE_API_ENUMS,
  multiMediaUploadKeys,
} from 'utils/constants/media';
import isEmpty from 'lodash/isEmpty';
import API from 'services/api';
import { assetStatus } from 'utils/constants/assetText';
import { filterListByKey } from 'utils/filterList';
import { transform } from 'utils/DataTransform/Asset/AssetList';
import { FollowupTaskTableHeaders } from 'utils/constants/task';
import TemplateForm from 'containers/Pages/Tasks/Template/form';
import { transformFormViewRootTask } from 'utils/DataTransform/Task/subTask';
import { FORM_STRINGS } from 'utils/constants/formStrings';
import { v4 } from 'uuid';
import { setSiteName } from 'utils/helper/site';
import { LOW_DETAIL_CONFIG } from 'utils/constants/Map';

export const severityRatingEnums = {
  notRated: 'NOT_RATED',
  veryLow: 'VERY_LOW',
  low: 'LOW',
  medium: 'MEDIUM',
  high: 'HIGH',
  veryHigh: 'VERY_HIGH',
};

export const severityRatingOptions = [
  { id: severityRatingEnums.notRated, name: 'Not Rated' },
  { id: severityRatingEnums.veryLow, name: 'Very Low' },
  { id: severityRatingEnums.low, name: 'Low' },
  { id: severityRatingEnums.medium, name: 'Medium' },
  { id: severityRatingEnums.high, name: 'High' },
  { id: severityRatingEnums.veryHigh, name: 'Very High' },
];

export const converSeverityRatingToText = constant => {
  const ratingEnums = {
    [severityRatingEnums.notRated]: 'Not Rated',
    [severityRatingEnums.veryLow]: 'Very Low',
    [severityRatingEnums.low]: 'Low',
    [severityRatingEnums.medium]: 'Medium',
    [severityRatingEnums.high]: 'High',
    [severityRatingEnums.veryHigh]: 'Very High',
  };

  return ratingEnums[constant] ?? ratingEnums[Object.keys(ratingEnums)[0]];
};

export const getEventCategoryByServices = services => {
  if (isEmpty(services)) {
    return [];
  }
  const filteredEventCategories = eventTypes.reduce(
    (serviceSpecificEventType, currentType) => {
      // has services array and are not deprecated
      if (currentType.services && !currentType.deprecated) {
        // if any eventCategoryType has services then check if they exists in the company service list
        const ifCategoryHasService = currentType.services.some(f =>
          services.includes(f),
        );
        if (ifCategoryHasService) {
          serviceSpecificEventType.push({
            id: currentType.category,
            name: `${t(currentType.category)} (${t(
              currentType.defaultDistributionName,
            )})`,
          });
        }
      } else if (
        // eslint-disable-next-line no-prototype-builtins
        !currentType.hasOwnProperty('services') &&
        // eslint-disable-next-line no-prototype-builtins
        !currentType.hasOwnProperty('deprecated')
      ) {
        // if there is no service array and is not deprecated then it is a generic type
        serviceSpecificEventType.push({
          id: currentType.category,
          name: `${t(currentType.category)} (${t(
            currentType.defaultDistributionName,
          )})`,
        });
      }
      return serviceSpecificEventType;
    },
    [],
  );
  return filteredEventCategories;
};

export const validationSchema = minDate =>
  validationObject({
    title: validateName(250),
    eventCategory: validateRequired(),
    eventType: validateRequired(),
    credibility: validateRequired(),
    publicDescription: validateRequired(),
    startTime: validateDateRequired(),
    locations: validateRequired(),
    ...(minDate && { finishTime: validateEndDate(minDate) }),
  });

// eslint-disable-next-line no-unused-vars
export const initialValues = data => {
  const displayedValues = {
    ...data.generalInfo,
    ...data.detailedInfo,
    ...data.others,
  };

  const initial = {};
  Object.entries(displayedValues).forEach(key => {
    initial[key[0]] = key[1].value;
  });
  return initial;
};

export const initialState = {
  eventCategory: '',
  eventType: '',
  credibility: 5,
  reporters: [],
  involvedPeople: [],
  managerialOnlyDistribution: false,
  title: '',
  severityRating: severityRatingEnums.notRated,
};

export const overviewSection = ({ startTime, finishTime }) => {
  return [
    {
      field: 'publicDescription',
      label: 'Summary',
      required: true,
      type: 'textArea',
      maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH,
      info: EventInfo.public,
      placeholder: EventPlaceholder.public,
    },
    {
      field: 'startTime',
      type: 'datetime',
      size: 'half',
      label: 'Incident Start Time',
      required: true,
      info: EventInfo.start,
      maxDate: finishTime,
    },
    {
      field: 'finishTime',
      type: 'datetime',
      size: 'half',
      label: 'Incident Finish Time',
      minDate: startTime,
      info: EventInfo.end,
    },
  ];
};

export const locationSection = ({
  eventCategory,
  sitePolygon,
  securityCompanyId,
  disabledFields,
}) => {
  return [
    {
      field: 'site',
      type: 'async-select',
      required: false,
      disabled: disabledFields && disabledFields.includes('site'),
      size: 'full',
      valueType: 'object',
      fetchOptions: API.fetchSitesList,
      fetchFilters: {
        securityCompanyId,
        archived: false,
      },
      optionLabelKey: 'siteName',
      dataTransform: ({ data }) => {
        const transformedNameSites = data.map(site => ({
          id: site.id,
          siteName: setSiteName(site),
          name: site.name,
          siteId: site.siteId,
          bizId: site?.businessClient.id,
          businessClientId: site?.businessClient.businessClientId,
          businessClientName: site?.businessClient.businessClientName,
          boundary: site.boundary,
        }));
        return transformedNameSites;
      },
    },
    {
      field: 'locations',
      type: 'map-polygon',
      required: true,
      eventType: eventCategory,
      info: EventInfo.locations,
      centreOfGravity: sitePolygon && {
        lat: sitePolygon.centreOfGravity.latitude,
        lng: sitePolygon.centreOfGravity.longitude,
      },
      secondaryPolygons: sitePolygon // these are the polygons that are for showing purposes without any edit or drag capabilities
        ? [sitePolygon].map(polygon => ({
            paths: polygon.points.map(p => ({
              lat: p.latitude,
              lng: p.longitude,
            })),
            isFilled: false,
            color: 'black',
            ...LOW_DETAIL_CONFIG,
          }))
        : [],
    },
  ];
};

export const descriptionSection = () => {
  return [
    {
      field: 'whatHappened',
      type: 'textArea',
      label: 'What happened?',
      maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH,
      info: EventInfo.what,
      placeholder: EventPlaceholder.internal,
    },
    {
      field: 'whereHappened',
      type: 'textArea',
      label: 'Where did it happen?',
      maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH,
      info: EventInfo.where,
      placeholder: EventPlaceholder.internal,
    },
    {
      field: 'whenHappened',
      type: 'textArea',
      label: 'When did it happen?',
      maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH,
      info: EventInfo.when,
      placeholder: EventPlaceholder.internal,
    },
    {
      field: 'whoHappened',
      type: 'textArea',
      label: 'Who was involved?',
      maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH,
      info: EventInfo.who,
      placeholder: EventPlaceholder.internal,
    },
    {
      field: 'whyHappened',
      type: 'textArea',
      label: 'Why did it happen?',
      maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH,
      info: EventInfo.why,
      placeholder: EventPlaceholder.internal,
    },
    {
      field: 'howHappened',
      type: 'textArea',
      label: 'How did it happen?',
      maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH,
      info: EventInfo.how,
      placeholder: EventPlaceholder.internal,
    },

    {
      field: 'internalDescription',
      type: 'textArea',
      label: 'Internal Notes',
      maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH,
      info: EventInfo.internal,
      placeholder: EventPlaceholder.internal,
    },
  ];
};
export const originalReportOverviewSection = ({ parentEvent, services }) => {
  return parentEvent
    ? [
        {
          field: 'parentEventId',
          type: 'text',
          size: 'half',
          disabled: true,
          placeholder: 'Value is set by the system.',
        },
        {
          field: 'parentEventTitle',
          type: 'text',
          size: 'half',
          disabled: true,
        },
        {
          field: 'parentEventCategory',
          type: 'select',
          size: 'half',
          options: getEventCategoryByServices(services),
          required: true,
          disabled: true,
          info: EventInfo.category,
        },
        {
          field: 'parentEventType',
          type: 'select',
          size: 'half',
          options: [],
          required: true,
          info: EventInfo.types,
          disabled: true,
        },
        {
          field: 'severityRating',
          type: 'select',
          label: 'Severity Rating',
          options: severityRatingOptions,
          size: 'half',
          disabled: true,
        },
        {
          field: 'parentSeverity',
          type: 'text',
          label: 'Severity Description',
          maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH,
          disabled: true,
          placeholder: EventPlaceholder.severity,
        },
        {
          field: 'parentStartTime',
          type: 'datetime',
          size: 'half',
          label: 'Incident Start Time',
          required: true,
          info: EventInfo.start,
          maxDate: parentEvent.finishTime,
          disabled: true,
        },
        {
          field: 'parentFinishTime',
          type: 'datetime',
          size: 'half',
          label: 'Incident Finish Time',
          minDate: parentEvent.startTime,
          info: EventInfo.end,
          disabled: true,
        },
        {
          field: 'parentSummary',
          label: 'Summary',
          required: true,
          type: 'textArea',
          maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH,
          disabled: true,
        },
        {
          field: 'parentWhat',
          type: 'textArea',
          label: 'What happened?',
          disabled: true,
          info: EventInfo.what,
        },
        {
          field: 'parentWhere',
          type: 'textArea',
          label: 'Where did it happen?',
          disabled: true,
          info: EventInfo.where,
        },
        {
          field: 'parentWhen',
          type: 'textArea',
          label: 'When did it happen?',
          disabled: true,
          info: EventInfo.when,
        },
        {
          field: 'parentWhy',
          type: 'textArea',
          label: 'Why did it happen?',
          disabled: true,
          info: EventInfo.why,
        },
        {
          field: 'parentHow',
          type: 'textArea',
          label: 'How did it happen?',
          disabled: true,
          info: EventInfo.how,
        },
      ]
    : null;
};

export const noSectionFields = ({
  eventCategory,
  services,
  editMode,
  disabledFields,
}) => {
  return [
    {
      field: 'eventId',
      label: 'Report ID',
      type: 'text',
      size: 'half',
      disabled: true,
      placeholder: 'Value is set by the system.',
    },
    ...optionalFormField({
      showWhen: !editMode,
      field: [
        {
          field: 'managerialOnlyDistribution',
          type: 'toggle',
          size: 'half',
        },
      ],
    }),
    {
      field: 'title',
      type: 'text',
      required: true,
      maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH,
      size: 'full',
      placeholder: EventPlaceholder.title,
      info: EventInfo.title,
    },
    {
      field: 'eventCategory',
      type: 'select',
      size: 'half',
      options: getEventCategoryByServices(services),
      required: true,
      disabled: disabledFields && disabledFields.includes('eventCategory'),
      info: EventInfo.category,
      updateDependentField: [
        // if this component is updated, always clear eventtype
        {
          key: 'eventType',
          passValue: '', // assign this value
          condition: value => value, // always true, therefore, it will ensure that eventType will be deselected when category is changed
        },
      ],
    },
    {
      field: 'eventType',
      type: 'async-select',
      size: 'half',
      fetchOptions: API.fetchEventTypes,
      disabled: eventCategory === '',
      fetchFilters: {
        archived: false,
        category: eventCategory,
      },
      optionLabelKey: 'typeName',
      dataTransform: ({ data }) => {
        return data.map(m => ({
          ...m,
          id: m.id !== '' ? m.id : v4(), // assign the id as the static categories does not have id
        }));
      },
      resetWhen: [
        {
          value: eventCategory,
          condition: val => {
            return val;
          },
        },
      ],
      required: true,
      // info: EventInfo.types,
    },
    {
      field: 'severityRating',
      type: 'select',
      label: 'Severity Rating',
      options: severityRatingOptions,
      size: 'half',
    },
    {
      field: 'severity',
      type: 'text',
      size: 'half',
      label: 'Severity Description',
      maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH,
      placeholder: EventPlaceholder.severity,
    },
  ];
};

export const incidentReviewSection = ({
  editMode,
  parentEventId,
  updatedValues,
}) => [
  ...optionalFormField({
    showWhen: editMode,
    field: {
      type: 'embedded-form-follow-up',
      field: 'followupEvents',
      label: 'Follow-up Reports',
      component: FollowupEventForm,
      archiveDelete: true,
      columns: FollowupReprtTableColumns,
      updatedValues,
      componentProps: {
        parentEventId,
        disablePresist: true,
        defaultFieldValues: {
          // default fields for follow tasks carried over from its parent event
          publicDescription: updatedValues?.publicDescription,
          startTime: updatedValues.startTime,
          ...(updatedValues?.finishTime && {
            finishTime: updatedValues?.finishTime,
          }),
          locations: updatedValues?.locations,
          eventCategory: updatedValues.eventCategory,
          eventType: {
            ...updatedValues.eventType,
            defaultDescription: null,
            archived: false,
            category: updatedValues?.eventCategory,
          },
        },
      },
      dataTransform: data => transformFollowUpEvent(data),
      deleteReferenceKey: 'general.id',
    },
  }),
  ...optionalFormField({
    showWhen: editMode,
    field: {
      type: 'embedded-form-follow-up',
      field: 'followUptask',
      label: 'Follow Up Tasks',
      component: TemplateForm,
      archiveDelete: true,
      columns: FollowupTaskTableHeaders,
      updatedValues,
      componentProps: {
        isTemplate: false,
        fromEvents: true,
        multiSchedule: false,
        disablePresist: true,
      },
      dataTransform: data => transformFormViewRootTask(data),
    },
  }), // this should not show if the current event does not have an id
  {
    field: 'recommendations',
    type: 'sub-form',
    formHeader: 'Add new recommendation',
    info: EventInfo.recommendations,
    subFields: [
      {
        field: 'audience',
        type: 'select',
        required: true,
        options: audienceList,
        size: 'large',
        placeholder: 'Audience',
      },
      {
        field: 'description',
        type: 'textArea',
        maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH,
        required: true,
        size: 'large',
        placeholder: 'Description',
      },
    ],
    defaultValues: {
      audience: '',
      description: '',
    },
    requiredFields: ['audience', 'description'],
  },
];

export const auditTrailSection = ({ auditStatus }) => [
  {
    field: 'auditStatus',
    type: 'table',
    columns: AuditTableColumns,
    dataTransform: data => auditTableTransform(data),
    tableValues: auditStatus,
  },
];

//eslint-disable-next-line max-params
export const baseDetailedSection = (
  formValues,
  disabledFields,
  securityCompanyId,
) => [
  {
    field: 'websites',
    type: 'sub-form',
    formHeader: 'Add new source: website',
    titleKey: 'companyName',
    info: EventInfo.websites,
    subFields: [
      {
        field: 'credibility',
        label: 'Credibility',
        type: 'rating',
        required: true,
        size: 'large',
        info: EventInfo.reliability,
      },
      {
        field: 'url',
        label: 'URL',
        type: 'text',
        required: true,
        size: 'large',
        maxLength: FORM_STRINGS.MAX_TEXTAREA_FIELD_LENGTH8,
        placeholder: 'URL',
      },
      {
        field: 'companyName',
        type: 'text',
        size: 'large',
        maxLength: 30,
        placeholder: EventPlaceholder.companyName,
        info: EventInfo.company,
      },
    ],
    defaultValues: {
      credibility: '',
      url: '',
      companyName: '',
    },
    requiredFields: ['url', 'credibility'],
  },
  {
    field: 'involvedAssets',
    type: 'sub-form',
    formHeader: 'Add new involved asset',
    titleKey: 'assetName',
    info: EventInfo.assets,
    disabled: disabledFields && disabledFields.includes('involvedAssets'),
    subFields: [
      { field: 'category' },
      { field: 'typeName' },
      { field: 'assetId' },
      {
        field: 'assetName',
        type: 'async-select',
        required: true,
        maxLength: 30,
        size: 'large',
        fetchOptions: API.getAssetList,
        fetchFilters: { securityCompanyId, archived: false },
        optionLabelKey: 'name',
        dataTransform: ({ data }) => {
          // transform AssetList
          const transformedAssetList = transform(data);
          // get assets by required keys
          const getAssetsByKey = filterListByKey(
            transformedAssetList,
            [
              assetStatus.AVAILABLE,
              assetStatus.DEPLOYING,
              assetStatus.IN_USE,
              assetStatus.RETURNING,
              assetStatus.UNAVAILABLE,
            ],
            'status',
          ).map(asset => ({
            ...asset,
            category: asset.category.id, // since category in the transform is in the form of an object {id:'', icon:'', name:''}
            // to display here, we just need  a category string...
            assetCategoryName: asset.typeName,
          }));

          return getAssetsByKey;
        },
      },
      { field: 'id', display: 'none' },
      { field: 'typeId', display: 'none' },
      { field: 'typeName' },
      { field: 'originalTypeName', display: 'none' },
    ],
    exception: 'assetName',
    defaultValues: {
      assetName: '',
    },
    requiredFields: ['assetName'],
  },
  {
    field: 'involvedPeople',
    type: 'sub-form',
    titleKey: 'firstName',
    formHeader: 'Add new involved person',
    info: EventInfo.involvedPeople,
    subFields: [
      {
        field: 'involvement',
        type: 'select',
        required: true,
        options: involvementList,
        size: 'large',
        placeholder: 'Involvement',
        info: EventInfo.involvement,
      },
      {
        field: 'firstName',
        type: 'text',
        required: true,
        maxLength: 30,
        size: 'large',
        placeholder: 'First Name',
      },
      {
        field: 'lastName',
        type: 'text',
        maxLength: 30,
        required: true,
        size: 'large',
        placeholder: 'Last Name',
      },
      {
        field: 'jobRole',
        type: 'text',
        maxLength: 30,
        size: 'large',
        placeholder: 'Enter job role.',
      },
      {
        field: 'companyName',
        type: 'text',
        maxLength: 30,
        size: 'large',
        placeholder: 'Company Name',
      },
    ],
    defaultValues: {
      involvement: '',
      firstName: '',
      lastName: '',
      jobRole: '',
      companyName: '',
    },
    requiredFields: ['involvement', 'firstName', 'lastName'],
  },
  {
    field: 'involvedCompanies',
    type: 'sub-form',
    formHeader: 'Add new involved company',
    titleKey: 'name',
    info: EventInfo.involvedCompanies,
    disabled: disabledFields && disabledFields.includes('involvedCompanies'),
    subFields: [
      {
        field: 'name',
        type: 'text',
        required: true,
        maxLength: 30,
        size: 'large',
        placeholder: 'Company Name',
      },
    ],
    defaultValues: {
      name: '',
    },
    requiredFields: ['name'],
  },
  {
    field: 'media',
    type: 'multiMediaUpload',
    label: multiMediaUploadKeys.label,
    info: EventInfo.media,
    required: false,
    mediaHeader: multiMediaUploadKeys.multiMediaUploadHeader,
    mediaType: [MEDIA_TYPE_API_ENUMS.IMAGE, MEDIA_TYPE_API_ENUMS.AUDIO],
    mediaLabel: multiMediaUploadKeys.uploaderlabel,
    disabled: formValues?.media ? formValues.media.length >= 10 : false,
    noOfMediaFilesAllowed: 10,
  },
];

export const detailedSection = ({
  formValues,
  // taskProps,
  // activeAssets,
  securityCompanyId,
  disabledFields,
}) => {
  return baseDetailedSection(
    formValues,
    // taskProps,
    disabledFields,
    securityCompanyId,
  );
};
